import { MetaFunction } from '@remix-run/node'
import { Link } from '@remix-run/react'
import {
  LucideMail,
  LucidePhone,
  LucideSmartphone,
  LucideUser,
} from 'lucide-react'
import { useMemo, useState } from 'react'
import { useAdminUsersQuery } from '~/api'
import { LoadingCircle } from '~/components'
import { Button, Input } from '~/components/ui'
import { useDebounce, useFuseSearch } from '~/hooks'
import { UserRole } from '~/models'
import { fmtName, fmtPhoneNumber } from '~/utils'

export const meta: MetaFunction = () => [{ title: 'Brokers - Associate' }]

export default function AppBrokersRoute() {
  const [query, setQuery] = useState('')
  const debouncedQuery = useDebounce(query, 250)
  const [numberOfBrokers, setNumberOfBrokers] = useState<number>(12)
  const { isLoading, users } = useAdminUsersQuery(['admin', 'users'], {
    status: 'active',
    role: {
      $in: [UserRole.broker, UserRole.managingBroker],
    },
  })

  const filteredBrokers = useFuseSearch({
    query: debouncedQuery,
    list: users,
    keys: [
      {
        name: 'name',
        getFn: (user) =>
          fmtName(user.firstName, user.middleName, user.lastName),
      },
      'email',
      'phone',
      'company.name',
    ],
  })

  const visibleBrokers = useMemo(() => {
    return filteredBrokers.slice(0, numberOfBrokers)
  }, [filteredBrokers, numberOfBrokers])

  const handleLoadMoreClick = () => {
    const totalCompanies = numberOfBrokers + 24
    return setNumberOfBrokers(totalCompanies)
  }

  const renderContent = () => {
    if (isLoading && users.length === 0) {
      return (
        <div className="mb-[200px] flex w-full flex-col items-center py-6">
          <LoadingCircle className="mb-3 h-8 w-8 text-primary" /> Loading
          Brokers
        </div>
      )
    }
    return (
      <>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3 lg:gap-8">
          {visibleBrokers.map((broker) => {
            return (
              <div
                key={broker.tid}
                className="rounded-md border-2 border-muted bg-background/50 p-6 shadow-md sm:p-8">
                <div className="mx-16 mb-6 flex aspect-square items-center justify-center rounded-full bg-muted shadow-sm">
                  {broker.meta?.photo ? (
                    <img
                      src={broker.meta.photo.url}
                      className="h-full w-full rounded-full object-cover"
                    />
                  ) : (
                    <LucideUser className="h-20 w-20 text-muted-foreground" />
                  )}
                </div>
                <h3 className="text-lg">
                  <Link
                    to={`/brokers/${broker.slug}`}
                    target="_blank"
                    className="hover:underline hover:underline-offset-4">
                    {fmtName(
                      broker.firstName,
                      broker.middleName,
                      broker.lastName
                    )}
                  </Link>
                </h3>
                {broker.company && (
                  <h2 className="mb-4 text-sm">
                    <Link
                      to={`/companies/${broker.company?.slug}`}
                      target="_blank"
                      className="hover:underline hover:underline-offset-4">
                      {broker.company?.name}
                    </Link>
                  </h2>
                )}
                {broker.meta?.description && (
                  <div
                    className="mb-4 line-clamp-4"
                    dangerouslySetInnerHTML={{
                      __html: broker.meta!.description,
                    }}></div>
                )}
                {broker.email && (
                  <div className="flex items-center">
                    <LucideMail className="mr-3 h-5 w-5 shrink-0 text-foreground" />
                    <a
                      href={`mailto:${broker.email}`}
                      target="_blank"
                      className="overflow-hidden overflow-ellipsis px-0 text-base font-normal text-primary underline-offset-4 hover:underline">
                      {broker.email}
                    </a>
                  </div>
                )}
                {broker.meta?.office && (
                  <div className="mt-2 flex items-center">
                    <LucidePhone className="mr-3 h-5 w-5 text-foreground" />

                    <a
                      href={`tel:${broker.meta?.office}`}
                      target="_blank"
                      className="px-0 text-base font-normal text-primary underline-offset-4 hover:underline">
                      {fmtPhoneNumber(broker.meta?.office)}
                    </a>
                  </div>
                )}
                {broker.meta?.mobile && (
                  <div className="mt-2 flex items-center">
                    <LucideSmartphone className="mr-3 h-5 w-5 text-foreground" />
                    <a
                      href={`tel:${broker.meta?.mobile}`}
                      target="_blank"
                      className="px-0 text-base font-normal text-primary underline-offset-4 hover:underline">
                      {fmtPhoneNumber(broker.meta?.mobile)}
                    </a>
                  </div>
                )}
              </div>
            )
          })}
        </div>
        {visibleBrokers.length === 0 && (
          <div className="my-10 flex items-center justify-center">
            {isLoading ? (
              <div className="flex flex-col justify-center">
                <LoadingCircle className="mb-3 h-8 w-8 text-primary" /> Loading
                Brokers
              </div>
            ) : (
              <p>No brokers found</p>
            )}
          </div>
        )}
        <div className="my-6 flex justify-center">
          {visibleBrokers.length >= numberOfBrokers && (
            <Button onClick={handleLoadMoreClick}>Load More</Button>
          )}
        </div>
      </>
    )
  }
  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-6">
      <div className="mt-16 px-4 md:px-6 lg:px-8">
        <div className="mx-auto max-w-6xl">
          <div className="flex justify-between">
            <h2 className="mb-16 text-left text-2xl sm:text-3xl">Brokers</h2>
            <Input
              placeholder="Search Brokers..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="h-9 w-64"></Input>
          </div>
          {renderContent()}
        </div>
      </div>
    </div>
  )
}
